import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { NotesComposer } from './NotesComposer';
import type { Drn } from '@duke-hq/drn/type';
import { Box, Center, Flex, Stack, VStack } from '@chakra-ui/react';
import { Spinner } from '@chakra-ui/spinner';
import { Trans } from '@lingui/react/macro';
import { useEffect, useRef } from 'react';
import { Note } from './Note';
import { PinnedNotes } from './components/PinnedNotes';

export function NoteList({
  userDrn,
  tenantId,
  consultantId,
}: {
  userDrn: Drn;
  tenantId: number;
  consultantId?: string;
}) {
  const { data: notes, isLoading: notesLoading } = trpcAdmin.students.adminNotes.useQuery({
    drn: userDrn,
  });
  const trpcUtils = trpcAdmin.useUtils();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Custom scroll to bottom that accounts for PinnedNotes
  useEffect(() => {
    if (notes?.length && !notesLoading && scrollRef.current) {
      // Small delay to ensure layout is settled
      setTimeout(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [notes?.length, notesLoading]);

  const pinnedNotes = notes
    ?.filter((note) => note.pinnedAt)
    .sort((a, b) => {
      if (a.pinnedAt != null && b.pinnedAt != null) {
        return new Date(b.pinnedAt).getTime() - new Date(a.pinnedAt).getTime();
      }
      return 0;
    });

  if (notesLoading) {
    return (
      <Center height="100vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <Flex direction="column" height="100%" bg="bg.muted">
      <Box flex="1" overflowY="auto" overflowX="hidden" p={2} ref={scrollRef} minHeight={0}>
        <Stack mx="auto" px={{ base: 4, md: 0 }} spacing={0} pb={4}>
          {notes?.length ? (
            <>
              {notes.map((note) => (
                <Flex
                  key={note.id}
                  justify={note.note_type.includes('OUTBOUND') ? 'flex-end' : 'flex-start'}
                  my={2}
                >
                  <Note {...note} />
                </Flex>
              ))}
            </>
          ) : (
            <Center minHeight="200px">
              <VStack spacing={2}>
                <Trans>No notes available</Trans>
              </VStack>
            </Center>
          )}
          <Box mt={4}>
            <NotesComposer
              userDrn={userDrn}
              tenantId={tenantId}
              consultantId={consultantId}
              refetchNotes={async () => trpcUtils.students.adminNotes.refetch()}
            />
          </Box>
        </Stack>
      </Box>
      {pinnedNotes?.length ? <PinnedNotes notes={pinnedNotes} /> : null}
    </Flex>
  );
}
